/**
 * One audio-gap marker in a transcript (#14375).
 *
 * A stretch of the recording nobody captured — a tab that closed with audio
 * still in memory, a pause, a chunk that never stored. It renders as its own
 * line between the paragraphs either side, because a transcript that reads as
 * continuous over a hole is exactly the silent gap epic #14371 rules out.
 *
 * A list item: it sits in the transcript's ordered list, in timeline order,
 * so a screen reader meets it where the audio stopped. Not interactive.
 */

import { MicOff } from '@/components/icons'
import { formatTimecode } from '@/lib/meetings/record-page-model'
import type { AudioGap } from '@/lib/notetaker/audio-gaps'

export function TranscriptAudioGap({ gap }: { gap: AudioGap }) {
  const from = formatTimecode(gap.startMs) ?? '0:00'
  const to = formatTimecode(gap.endMs) ?? '0:00'

  return (
    <li
      data-testid="transcript-audio-gap"
      className="flex items-center gap-2 rounded-md border border-dashed border-stroke-light bg-surface-panel px-3 py-2 text-xs text-text-secondary"
    >
      <MicOff
        className="h-4 w-4 shrink-0 text-text-secondary"
        aria-hidden="true"
      />
      <span>
        No audio captured, {from}–{to}
      </span>
    </li>
  )
}
